/** @jsxImportSource @emotion/react */
import React from 'react'
import { css } from '@emotion/react'
import colors from '@sportninja/common/constants/appColors'
import type { DayOfWeekSelectorProps } from './types'

// Day labels matching the design (single letter)
const DAYS = [
  { value: 0, label: 'S' }, // Sunday
  { value: 1, label: 'M' }, // Monday
  { value: 2, label: 'T' }, // Tuesday
  { value: 3, label: 'W' }, // Wednesday
  { value: 4, label: 'T' }, // Thursday
  { value: 5, label: 'F' }, // Friday
  { value: 6, label: 'S' }, // Saturday
]

/**
 * Day of week selector with toggle buttons
 * Matches Figma design with S M T W T F S pills
 */
const DayOfWeekSelector: React.FC<DayOfWeekSelectorProps> = ({
  selectedDays,
  onChange,
  disabled = false,
}) => {
  const handleToggle = (dayValue: number) => {
    if (disabled) {
      return
    }

    if (selectedDays.includes(dayValue)) {
      onChange(selectedDays.filter((d) => d !== dayValue))
    } else {
      onChange([...selectedDays, dayValue].sort((a, b) => a - b))
    }
  }

  return (
    <div
      css={css`
        display: flex;
        align-items: center;
        gap: 8px;
      `}
    >
      {DAYS.map((day) => {
        const isSelected = selectedDays.includes(day.value)
        return (
          <button
            key={day.value}
            type='button'
            onClick={() => handleToggle(day.value)}
            disabled={disabled}
            css={css`
              display: flex;
              align-items: center;
              justify-content: center;
              width: 32px;
              height: 32px;
              padding: 8px;
              border-radius: 8px;
              border: none;
              cursor: ${disabled ? 'not-allowed' : 'pointer'};
              background-color: ${isSelected
                ? colors.SECONDARY_75
                : colors.SECONDARY_600};
              color: ${colors.NEUTRAL_0};
              font-family: 'Inter', sans-serif;
              font-size: 14px;
              font-weight: ${isSelected ? 700 : 400};
              line-height: 20.3px;
              text-align: center;
              transition: all 0.15s ease;
              opacity: ${disabled ? 0.5 : 1};

              &:hover:not(:disabled) {
                background-color: ${isSelected
                  ? colors.SECONDARY_75
                  : colors.SECONDARY_400};
              }
            `}
            aria-pressed={isSelected}
            aria-label={`${
              [
                'Sunday',
                'Monday',
                'Tuesday',
                'Wednesday',
                'Thursday',
                'Friday',
                'Saturday',
              ][day.value]
            } ${isSelected ? 'selected' : 'not selected'}`}
          >
            {day.label}
          </button>
        )
      })}
    </div>
  )
}

export default DayOfWeekSelector
